import useRequest from "@/lib/request/useRequest";
import { faPlus, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Accordion,
    AccordionItem,
    Button,
    Checkbox,
    Input,
    Slider,
    Tabs,
    Tab,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    DataLayerDetail,
    DataLayerType,
    GeneratedZones,
} from "@/types/dataLayer";
import {
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
} from "react-hook-form";
import { InferType, array, boolean, number, object, string } from "yup";
import { HarvestOperation } from "@/types/operations";
import { Imagery } from "@/types/imagery";
import { yupResolver } from "@hookform/resolvers/yup";
import SelectInput from "@/components/inputs/SelectInput";
import { useQueryClient } from "@tanstack/react-query";
import TextInput from "@/components/inputs/TextInput";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import {
    FillLayerSpecification,
    GeoJSONSource,
    LineLayerSpecification,
} from "mapbox-gl";
import classNames from "classnames";
import { MZ_COLORS } from "@/lib/map/layers";
import UploadDataLayer from "./UploadDataLayer";
import { useTranslation } from "react-i18next";
import { Feature, MultiPolygon } from "geojson";
import dayjs from "dayjs";

const schema = object({
    min_zones: number().required(),
    max_zones: number().required(),
    selected_layers: array().of(
        object({
            type: string().required(),
            layer: string().required(),
            attribute: string().required(),
            weight: number().required(),
            isDataLayer: boolean(),
        }),
    ),
});

const SELECTABLE_LAYER_TYPES = [
    { key: "harvest", label: "Harvest" },
    { key: "image", label: "Imagery" },
    { key: "soil sample", label: "Soil Sample" },
    { key: "soil map", label: "Soil Map" },
    { key: "topography", label: "Topography" },
    { key: "other", label: "Other" },
];

type FormType = InferType<typeof schema>;

function getGeneratedZoneSource(zone_number: number) {
    return `mz-creation-${zone_number}`;
}
function getGeneratedZoneFill(zone_number: number, isVisible?: boolean) {
    return {
        id: `mz-creation-layer-fill-${zone_number}`,
        type: "fill",
        source: getGeneratedZoneSource(zone_number),
        layout: {
            visibility: isVisible ? "visible" : "none",
        },
        paint: {
            "fill-color": ["get", "fill-color"],
            "fill-opacity": 0.8,
        },
    } as FillLayerSpecification;
}
function getGeneratedZoneLine(zone_number: number, isVisible?: boolean) {
    return {
        id: `mz-creation-layer-line-${zone_number}`,
        type: "line",
        source: getGeneratedZoneSource(zone_number),
        layout: {
            visibility: isVisible ? "visible" : "none",
        },
        paint: {
            "line-color": ["get", "fill-color"],
            "line-width": 3,
        },
    } as LineLayerSpecification;
}

function Row({
    index,
    options,
    length,
    remove,
}: {
    index: number;
    options: {
        attributes: { name: string }[];
        id: number;
        weight: number;
        layer_type: DataLayerType | string;
        name: string;
        isDataLayer: boolean;
    }[];
    length: number;
    remove: (index: number) => void;
}) {
    const f = useFormContext();
    const [type, layer] = f.watch([
        `selected_layers.${index}.type`,
        `selected_layers.${index}.layer`,
    ]);

    const layersWithType = options.filter(
        (option) => option.layer_type == type,
    );
    const selectedLayer = layersWithType.find(
        (filteredLayer) => filteredLayer.id.toString() === layer?.toString(),
    );

    useEffect(() => {
        if (selectedLayer) {
            f.setValue(
                `selected_layers.${index}.isDataLayer`,
                selectedLayer?.isDataLayer,
            );
        }
    }, [f, index, selectedLayer]);
    const attributes =
        selectedLayer?.attributes.map((attribute) => ({
            id: attribute.name,
            description: attribute.name,
        })) ?? [];

    return (
        <>
            <SelectInput
                size="sm"
                name={`selected_layers.${index}.type`}
                placeholder="Type"
                options={SELECTABLE_LAYER_TYPES.map((type) => ({
                    id: type.key,
                    description: type.label,
                }))}
                hideErrorMessage
            />
            <SelectInput
                size="sm"
                name={`selected_layers.${index}.layer`}
                placeholder="Layer"
                options={layersWithType.map((layer) => ({
                    id: layer.id,
                    description: layer.name,
                }))}
                isDisabled={layersWithType.length === 0}
                hideErrorMessage
            />
            <SelectInput
                size="sm"
                name={`selected_layers.${index}.attribute`}
                placeholder="Attribute"
                isDisabled={attributes.length === 0}
                options={attributes}
                hideErrorMessage
            />
            <TextInput
                size="sm"
                name={`selected_layers.${index}.weight`}
                placeholder="Weight"
                type="number"
                min={0}
                hideErrorMessage
            />
            <div className="flex flex-row ">
                <input
                    type="checkbox"
                    name={`selected_layers.${index}.isDataLayer`}
                    hidden
                />
                <input name={`selected_layers.${index}.id`} hidden />
                <Button
                    size="sm"
                    color="danger"
                    isDisabled={length === 1}
                    onPress={() => remove(index)}
                    isIconOnly
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </div>
        </>
    );
}

function CreationTool() {
    const { fieldId } = useFieldDashboard() ?? {};
    const { useMutateRequest, useGetRequest, axios } = useRequest();
    const { t } = useTranslation();

    const dataLayersQuery = useGetRequest<DataLayerDetail[]>({
        url: `fields/${fieldId}/data_layers/`,
        enabled: !!fieldId,
    });

    const harvestOperationsQuery = useGetRequest<HarvestOperation[]>({
        url: `fields/${fieldId}/harvest_operations/`,
        enabled: !!fieldId,
    });

    const favoriteImageryQuery = useGetRequest<Imagery[]>({
        url: `fields/${fieldId}/imagery/favorites/`,
        enabled: !!fieldId,
    });

    const zoneTool = useMutateRequest<GeneratedZones>({
        successMessage: t("success.zones_generated", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_generate_zones", {
            ns: "messages",
        }),
    });

    const [imageBasedZones, setImageBasedZones] = useState<boolean>(false);

    const onSubmit = useCallback(
        (data: FormType) => {
            if (imageBasedZones) {
                const paylod = {
                    field: Number(fieldId),
                    min_zones: Number(data.min_zones),
                    max_zones: Number(data.max_zones),
                    image_based_zones: true,
                };
                zoneTool.mutate({
                    url: `zone_tool/`,
                    method: "post",
                    data: paylod,
                });
                return;
            } else {
                const paylod: {
                    field: number;
                    min_zones: number;
                    max_zones: number;
                    zone_tool_datalayer: {
                        data_layer: string;
                        attribute: string;
                        weight: number;
                    }[];
                    zone_tool_harvest: {
                        harvest: string;
                        attribute: string;
                        weight: number;
                    }[];
                    zone_tool_image: {
                        image: string;
                        attribute: string;
                        weight: number;
                    }[];
                } = {
                    field: Number(fieldId),
                    min_zones: data.min_zones,
                    max_zones: data.max_zones,
                    zone_tool_datalayer: [],
                    zone_tool_harvest: [],
                    zone_tool_image: [],
                };

                data.selected_layers?.forEach((layer) => {
                    if (layer.isDataLayer) {
                        paylod.zone_tool_datalayer.push({
                            data_layer: layer.layer,
                            attribute: layer.attribute,
                            weight: layer.weight,
                        });
                    } else if (layer.type == "harvest" && !layer.isDataLayer) {
                        paylod.zone_tool_harvest.push({
                            harvest: layer.layer,
                            attribute: layer.attribute,
                            weight: layer.weight,
                        });
                    } else if (layer.type == "image" && !layer.isDataLayer) {
                        paylod.zone_tool_image.push({
                            image: layer.layer,
                            attribute: layer.attribute,
                            weight: layer.weight,
                        });
                    }
                });
                zoneTool.mutate({
                    url: `zone_tool/`,
                    method: "post",
                    data: paylod,
                });
            }
        },
        [fieldId, zoneTool, imageBasedZones],
    );

    const options = useMemo(() => {
        const dataLayers: {
            attributes: { name: string }[];
            id: number;
            weight: number;
            layer_type: DataLayerType | string;
            name: string;
            isDataLayer: boolean;
        }[] =
            dataLayersQuery.data?.map((layer) => ({
                ...layer,
                isDataLayer: true,
                weight: 1,
            })) ?? [];

        if (harvestOperationsQuery.data) {
            dataLayers.push(
                ...harvestOperationsQuery.data.map((operation) => ({
                    attributes: [{ name: "yield" }, { name: "moisture" }],
                    id: operation.id,
                    weight: 1,
                    layer_type: "harvest",
                    name: operation.start_date,
                    isDataLayer: false,
                })),
            );
        }
        if (favoriteImageryQuery.data) {
            dataLayers.push(
                ...favoriteImageryQuery.data.map((image) => ({
                    attributes: [{ name: "ndre" }],
                    id: image.id,
                    weight: 3,
                    layer_type: "image",
                    name: `${dayjs(image.capture_date).format("MM/DD/YYYY")} Image`,
                    isDataLayer: false,
                })),
            );
        }
        return dataLayers;
    }, [
        dataLayersQuery.data,
        favoriteImageryQuery.data,
        harvestOperationsQuery.data,
    ]);

    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            selected_layers: [
                {
                    type: "",
                    attribute: "",
                    weight: 1,
                    isDataLayer: false,
                },
            ],
            min_zones: 2,
            max_zones: 10,
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "selected_layers",
    });

    const [selectedZone, setSelectedZone] = useState<number | null>(null);
    const [selectedZoneFeatures, setSelectedZoneFeatures] = useState<
        Feature<MultiPolygon, { zone: number }>[] | null
    >(null);

    const { map, mapLoaded } = useMap();

    const onValueChanged = useCallback(
        (isChecked: boolean, zone_number: number) => {
            if (!map || !mapLoaded) return;
            if (isChecked) {
                const data =
                    zoneTool.data?.results
                        .find((result) => result.zones === zone_number)
                        ?.features?.map((feature, index) => ({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                "fill-color":
                                    MZ_COLORS[index % MZ_COLORS.length],
                            },
                        })) ?? [];
                if (!data) return;
                addUpdateGeojsonSource(
                    map,
                    getGeneratedZoneSource(zone_number),
                    {
                        type: "geojson",
                        data: {
                            type: "FeatureCollection",
                            features: data,
                        },
                        tolerance: 0,
                    },
                );
                addUpdateGeojsonLayers(map, [
                    getGeneratedZoneFill(zone_number, isChecked),
                    getGeneratedZoneLine(zone_number, isChecked),
                ]);
            } else {
                removeLayersIfExists(map, [
                    getGeneratedZoneFill(zone_number).id,
                    getGeneratedZoneLine(zone_number).id,
                ]);
                removeSourcesIfExists(map, [
                    getGeneratedZoneSource(zone_number),
                ]);
            }
        },
        [zoneTool.data, map, mapLoaded],
    );

    const onOpacityChanged = useCallback(
        (opacity: number, layerId: number) => {
            if (!map || !mapLoaded) return;
            const source = map.getSource(
                getGeneratedZoneSource(layerId),
            ) as GeoJSONSource;
            if (!source) return;

            map.setPaintProperty(
                getGeneratedZoneFill(layerId).id,
                "fill-opacity",
                opacity,
            );
            map.setPaintProperty(
                getGeneratedZoneLine(layerId).id,
                "line-opacity",
                opacity,
            );
        },
        [map, mapLoaded],
    );

    const createManagementZones = useMutateRequest({
        successMessage: t("success.management_zones_created", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_create_management_zones", {
            ns: "messages",
        }),
    });

    const [zoneName, setZoneName] = useState<string>("");
    const queryClient = useQueryClient();
    const onCreateManagementZones = useCallback(() => {
        if (imageBasedZones) {
            const data = {
                field: Number(fieldId),
                name: zoneName,
                is_primary: true,
                zones: selectedZoneFeatures,
            };
            createManagementZones.mutate({
                url: `management_zones/`,
                method: "post",
                data,
            });
            return;
        } else {
            const data = {
                zones: selectedZone,
                name: zoneName,
                smoothing_factor:
                    zoneTool.data?.results.find(
                        (result) => result.zones === selectedZone,
                    )?.smooth ?? 3,
            };
            createManagementZones.mutate(
                {
                    url: `zone_tool/${zoneTool.data?.id}/save/`,
                    method: "post",
                    data,
                },
                {
                    onSuccess: () => {
                        queryClient.refetchQueries({
                            queryKey: [`fields/${fieldId}/management_zones/`],
                            type: "active",
                            exact: true,
                        });
                        if (map && mapLoaded) {
                            removeLayersIfExists(map, [
                                getGeneratedZoneFill(
                                    zoneTool.data?.results.find(
                                        (result) =>
                                            result.zones === selectedZone,
                                    )?.zones ?? 0,
                                ).id,
                                getGeneratedZoneLine(
                                    zoneTool.data?.results.find(
                                        (result) =>
                                            result.zones === selectedZone,
                                    )?.zones ?? 0,
                                ).id,
                            ]);
                            removeSourcesIfExists(map, [
                                getGeneratedZoneSource(
                                    zoneTool.data?.results.find(
                                        (result) =>
                                            result.zones === selectedZone,
                                    )?.zones ?? 0,
                                ),
                            ]);
                            setSelectedZone(null);
                            setSelectedZoneFeatures(null);
                            setZoneName("");
                        }
                    },
                },
            );
        }
    }, [
        selectedZone,
        zoneName,
        createManagementZones,
        queryClient,
        fieldId,
        map,
        mapLoaded,
        zoneTool.data,
        selectedZoneFeatures,
        imageBasedZones,
    ]);

    return (
        <Accordion variant="splitted" className="p-0">
            <AccordionItem
                key="1"
                title={t("management_zones.management_zone_creation_tool", {
                    ns: "setup",
                })}
                indicator={<FontAwesomeIcon icon={faPlus} />}
            >
                {zoneTool.data ? (
                    <>
                        <div className="grid grid-cols-4 gap-1 text-sm font-semibold items-center">
                            <div>
                                {t("management_zones.rank", {
                                    ns: "setup",
                                })}
                            </div>
                            <div>
                                {t("management_zones.zone", {
                                    ns: "setup",
                                })}
                            </div>
                            <div>
                                {t("management_zones.smooth", {
                                    ns: "setup",
                                })}
                            </div>
                            <div>
                                {t("management_zones.view_compare", {
                                    ns: "setup",
                                })}
                            </div>
                        </div>
                        {zoneTool.data.results.map((result, index) => (
                            <div
                                key={result.zones}
                                className={classNames(
                                    "grid grid-cols-4 gap-1 text-sm hover:bg-gray-100 cursor-pointer mt-1 rounded-md items-center",
                                    {
                                        "bg-gray-200":
                                            selectedZone === result.zones,
                                    },
                                )}
                                onClick={() => {
                                    setSelectedZone(result.zones);
                                    setSelectedZoneFeatures(result.features);
                                }}
                            >
                                <div className="p-1">{index + 1}</div>
                                <div className="p-1">{result.zones}</div>
                                <div className="flex flex-row gap-1">
                                    <Input
                                        size="sm"
                                        value={result.smooth?.toString() ?? "3"}
                                        onChange={(e) => {
                                            zoneTool.data.results.map(
                                                (option) => {
                                                    if (
                                                        option.zones ===
                                                        result.zones
                                                    ) {
                                                        option.smooth =
                                                            parseInt(
                                                                e.target.value,
                                                            );
                                                    }
                                                },
                                            );
                                        }}
                                    />
                                    <Button
                                        size="sm"
                                        onPress={() => {
                                            axios
                                                .put<GeneratedZones>(
                                                    `zone_tool/${zoneTool.data?.id}/smooth/`,
                                                    {
                                                        [result.zones]:
                                                            result.smooth,
                                                    },
                                                )
                                                .then((resp) => {
                                                    const newResult =
                                                        resp.data.results.find(
                                                            (row) =>
                                                                row.zones ===
                                                                result.zones,
                                                        );
                                                    if (!newResult) return;
                                                    onValueChanged(
                                                        true,
                                                        result.zones,
                                                    );
                                                });
                                        }}
                                    >
                                        {t("management_zones.smooth", {
                                            ns: "setup",
                                        })}
                                    </Button>
                                </div>
                                <div className="flex flex-row gap-2">
                                    <Checkbox
                                        size="sm"
                                        aria-label="View Layer"
                                        onValueChange={(isChecked) => {
                                            onValueChanged(
                                                isChecked,
                                                result.zones,
                                            );
                                            if (isChecked) {
                                                setSelectedZone(result.zones);
                                                setSelectedZoneFeatures(
                                                    result.features,
                                                );
                                            } else {
                                                setSelectedZone(null);
                                                setSelectedZoneFeatures(null);
                                            }
                                        }}
                                    />
                                    <Slider
                                        size="sm"
                                        defaultValue={0.8}
                                        minValue={0}
                                        aria-label="Opacity"
                                        maxValue={1}
                                        step={0.01}
                                        onChange={(e) => {
                                            onOpacityChanged(
                                                e as number,
                                                result.zones,
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        <Input
                            size="sm"
                            label={t("management_zones.management_zone_name", {
                                ns: "setup",
                            })}
                            labelPlacement="outside"
                            placeholder={t(
                                "management_zones.management_zone_name",
                                {
                                    ns: "setup",
                                },
                            )}
                            isRequired
                            value={zoneName}
                            onChange={(e) => setZoneName(e.target.value)}
                        />
                        <Button
                            onPress={onCreateManagementZones}
                            isDisabled={
                                !selectedZone ||
                                createManagementZones.isPending ||
                                !zoneName
                            }
                            color="primary"
                            startContent={<FontAwesomeIcon icon={faPlus} />}
                            isLoading={createManagementZones.isPending}
                            size="sm"
                            className="mt-2"
                            fullWidth
                        >
                            {t("management_zones.create_management_zone", {
                                ns: "setup",
                            })}
                        </Button>
                    </>
                ) : (
                    <>
                        <Tabs
                            fullWidth
                            selectedKey={imageBasedZones ? "image" : "manual"}
                            onSelectionChange={(e) => {
                                setImageBasedZones(e === "image");
                            }}
                            className="mb-2"
                        >
                            <Tab key="manual" title="Custom Zones" />
                            <Tab key="image" title="Image Based Zones" />
                        </Tabs>
                        {!imageBasedZones && <UploadDataLayer dataLayerOnly />}
                        <FormProvider {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                {!imageBasedZones && (
                                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1 text-sm mt-2">
                                        <div>
                                            {t("management_zones.type", {
                                                ns: "setup",
                                            })}
                                        </div>
                                        <div>
                                            {t("management_zones.layer", {
                                                ns: "setup",
                                            })}
                                        </div>
                                        <div>
                                            {t("management_zones.attribute", {
                                                ns: "setup",
                                            })}
                                        </div>
                                        <div className="col-span-2">
                                            {t("management_zones.weight", {
                                                ns: "setup",
                                            })}
                                        </div>
                                        {fields.map((field, index) => {
                                            return (
                                                <Row
                                                    key={field.id}
                                                    index={index}
                                                    options={options}
                                                    length={fields.length}
                                                    remove={remove}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                                {!imageBasedZones && (
                                    <div className="flex flex-row gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            onPress={() =>
                                                append({
                                                    type: "",
                                                    layer: "",
                                                    attribute: "",
                                                    weight: 1,
                                                    isDataLayer: false,
                                                })
                                            }
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faPlus}
                                                />
                                            }
                                        >
                                            {t("management_zones.add_row", {
                                                ns: "setup",
                                            })}
                                        </Button>
                                    </div>
                                )}
                                <div className="flex flex-row gap-2 mb-2 mt-1">
                                    <TextInput
                                        size="sm"
                                        name="min_zones"
                                        labelPlacement="outside"
                                        label={t("management_zones.min_zones", {
                                            ns: "setup",
                                        })}
                                        placeholder={t(
                                            "management_zones.min_zones",
                                            {
                                                ns: "setup",
                                            },
                                        )}
                                        hideErrorMessage
                                    />
                                    <TextInput
                                        size="sm"
                                        name="max_zones"
                                        labelPlacement="outside"
                                        label={t("management_zones.max_zones", {
                                            ns: "setup",
                                        })}
                                        placeholder={t(
                                            "management_zones.max_zones",
                                            {
                                                ns: "setup",
                                            },
                                        )}
                                        hideErrorMessage
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    size="sm"
                                    color="primary"
                                    fullWidth
                                    startContent={
                                        <FontAwesomeIcon icon={faRefresh} />
                                    }
                                    isLoading={zoneTool.isPending}
                                    isDisabled={zoneTool.isPending}
                                    onPress={() => onSubmit(form.getValues())}
                                >
                                    {t(
                                        "management_zones.create_management_zone",
                                        {
                                            ns: "setup",
                                        },
                                    )}
                                </Button>
                            </form>
                        </FormProvider>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    );
}

export default CreationTool;
